import { Button, Flex, Text, Tooltip } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Badge, BadgeStatus } from '@aslanpay/portal-common-lib';

import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import { ReconciliationReportRowStatusValues } from 'app/constants/reconciliations';

import { ReconciliationReportRow } from 'app/types/reconciliations';

import {
  formatFindingType,
  formatReconciliationAmount as formatAmount,
} from 'app/utils/reconciliations';

interface ReconciliationReportColumnsOptions {
  onResolve?: (row: ReconciliationReportRow) => void;
  onAccept?: (row: ReconciliationReportRow) => void;
  fixedFindingIds?: Set<string>;
  acceptedFindingIds?: Set<string>;
  resolvedFindingIds?: Set<string>;
}

/**
 * @description Columns of a reconciliation run's report. The company column is left out in a company's own view, and
 * the actions to mark a problem as fixed or accept it are shown only when given; a problem already marked or accepted
 * shows that instead, and one a later run found fixed says so.
 */
function getReconciliationReportColumns(
  showCompanyColumn = true,
  {
    onResolve = undefined,
    onAccept = undefined,
    fixedFindingIds = new Set<string>(),
    acceptedFindingIds = new Set<string>(),
    resolvedFindingIds = new Set<string>(),
  }: ReconciliationReportColumnsOptions = {}
) {
  const columnHelper = createColumnHelper<ReconciliationReportRow>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<ReconciliationReportRow, any>[] = [
    columnHelper.accessor('status', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const status = ReconciliationReportRowStatusValues[info.getValue()];
        return (
          <Badge
            value={status?.value ?? info.getValue()}
            status={(status?.color ?? 'created') as BadgeStatus}
          />
        );
      },
    }),
    columnHelper.accessor('companyName', {
      header: 'Company',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('individualName', {
      header: 'Person',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('individualExternalPayrollId', {
      header: 'Payroll ID',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('type', {
      header: 'Problem',
      enableSorting: false,
      cell: (info) => (
        <Tooltip label={info.row.original.whatItMeans} placement="top" hasArrow>
          <Text>{formatFindingType(info.getValue())}</Text>
        </Tooltip>
      ),
    }),
    columnHelper.accessor('expectedAmount', {
      header: 'Expected',
      enableSorting: false,
      cell: (info) => formatAmount(info.getValue()),
    }),
    columnHelper.accessor('actualAmount', {
      header: 'Actual',
      enableSorting: false,
      cell: (info) => formatAmount(info.getValue()),
    }),
    columnHelper.accessor('difference', {
      header: 'Difference',
      enableSorting: false,
      cell: (info) => formatAmount(info.getValue()),
    }),
    columnHelper.accessor('daysOpen', {
      header: 'Days open',
      enableSorting: false,
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('detail', {
      header: 'Detail',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="360px" whiteSpace="normal">
          {info.getValue() || info.row.original.whatItMeans || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('findingId', {
      header: 'Finding ID',
      enableSorting: false,
      cell: (info) => (info.getValue() ? <TextWithClipboard value={info.getValue()} /> : '-'),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const { findingId } = info.row.original;
        if (!findingId) {
          return null;
        }
        if (acceptedFindingIds.has(findingId)) {
          return <Text color="brand.darkGray">Accepted</Text>;
        }
        if (fixedFindingIds.has(findingId)) {
          return <Text color="brand.darkGray">Marked as fixed</Text>;
        }
        if (resolvedFindingIds.has(findingId)) {
          return <Text color="brand.darkGray">Fixed</Text>;
        }
        return (
          <Flex gap="8px">
            {onResolve && (
              <Button
                size="xs"
                variant="outline"
                onClick={(event) => {
                  event.stopPropagation();
                  onResolve(info.row.original);
                }}
                data-testid={`mark-fixed-${findingId}`}
              >
                Mark as fixed
              </Button>
            )}
            {onAccept && (
              <Button
                size="xs"
                variant="outline"
                onClick={(event) => {
                  event.stopPropagation();
                  onAccept(info.row.original);
                }}
                data-testid={`accept-${findingId}`}
              >
                Accept
              </Button>
            )}
          </Flex>
        );
      },
    }),
  ];

  return columns.filter(
    (column) =>
      (showCompanyColumn || column.header !== 'Company') &&
      (onResolve !== undefined || onAccept !== undefined || column.id !== 'actions')
  );
}

export default getReconciliationReportColumns;
